import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CaretLeft, CaretRight, CookingPot, Copy, Plus, FilePdf, Sparkle, User } from '@phosphor-icons/react';
import ShareButton from '@/components/ShareButton';
import RecipePickerDialog from '@/components/RecipePickerDialog';
import ProgramMenuView from '@/components/ProgramMenuView';

const API = process.env.REACT_APP_BACKEND_URL;
const DAYS = ['segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado', 'domingo'];
const DAY_LABELS = { segunda: 'Segunda', terca: 'Terca', quarta: 'Quarta', quinta: 'Quinta', sexta: 'Sexta', sabado: 'Sabado', domingo: 'Domingo' };
const MEAL_LABELS = { sopa_almoco: 'Sopa (almoço)', almoco: 'Almoço', sopa_jantar: 'Sopa (jantar)', jantar: 'Jantar' };

function getMonday(d) {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  date.setDate(diff);
  return date.toISOString().split('T')[0];
}

function addWeeks(dateStr, weeks) {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + weeks * 7);
  return d.toISOString().split('T')[0];
}

function formatDateRange(weekStart) {
  const start = new Date(weekStart);
  const end = new Date(weekStart);
  end.setDate(end.getDate() + 6);
  const fmt = (d) => `${d.getDate()}/${d.getMonth() + 1}`;
  return `${fmt(start)} - ${fmt(end)}`;
}

export default function EmentaPage() {
  const [weekStart, setWeekStart] = useState(getMonday(new Date()));
  const [menu, setMenu] = useState(null);
  const [recipes, setRecipes] = useState([]);
  const [selectingMeal, setSelectingMeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [programMenu, setProgramMenu] = useState(null);
  const [view, setView] = useState('personalizada'); // 'personalizada' | 'fixa'

  const fetchMenu = useCallback(async () => {
    try {
      const { data } = await axios.post(`${API}/api/menus`, { week_start: weekStart }, { withCredentials: true });
      setMenu(data);
    } catch (err) {
      toast.error('Erro ao carregar ementa');
    } finally {
      setLoading(false);
    }
  }, [weekStart]);

  const fetchRecipes = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/recipes`, { withCredentials: true });
      setRecipes(data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const fetchProgramMenu = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/my-program-menu`, { withCredentials: true });
      setProgramMenu(data);
    } catch (err) {
      setProgramMenu(null);
    }
  }, []);

  useEffect(() => { fetchMenu(); }, [fetchMenu]);
  useEffect(() => { fetchRecipes(); }, [fetchRecipes]);
  useEffect(() => { fetchProgramMenu(); }, [fetchProgramMenu]);

  const hasFixedMenuContent = programMenu && (
    programMenu.weeks?.some(w => Object.values(w.days || {}).some(day => Object.values(day || {}).some(Boolean))) ||
    Object.values(programMenu.extra_sections || {}).some(arr => (arr || []).length > 0) ||
    (programMenu.note || '').trim()
  );

  const handleSelectRecipe = async (recipeId) => {
    if (!selectingMeal || !menu) return;
    try {
      await axios.put(`${API}/api/menus/${menu.id}`, {
        day: selectingMeal.day,
        meal_type: selectingMeal.mealType,
        recipe_id: recipeId
      }, { withCredentials: true });
      setSelectingMeal(null);
      fetchMenu();
      toast.success('Receita adicionada');
    } catch (err) {
      toast.error('Erro ao atualizar ementa');
    }
  };

  const handleClearMeal = async (day, mealType) => {
    if (!menu) return;
    try {
      await axios.put(`${API}/api/menus/${menu.id}`, {
        day, meal_type: mealType, recipe_id: null
      }, { withCredentials: true });
      fetchMenu();
    } catch (err) {
      toast.error('Erro ao limpar refeição');
    }
  };

  const handleDuplicate = async () => {
    if (!menu) return;
    const newWeek = addWeeks(weekStart, 1);
    try {
      await axios.post(`${API}/api/menus/${menu.id}/duplicate`, { week_start: newWeek }, { withCredentials: true });
      toast.success('Semana duplicada');
      setWeekStart(newWeek);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao duplicar');
    }
  };

  const getRecipeName = (recipeId) => {
    if (!recipeId) return null;
    const recipe = recipes.find(r => r.id === recipeId);
    return recipe?.name || 'Receita';
  };

  if (loading) {
    return <div className="text-center py-12 text-brand-muted" data-testid="ementa-loading">A carregar...</div>;
  }

  return (
    <div className="fade-in" data-testid="ementa-page">
      {/* Week navigation */}
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => setWeekStart(addWeeks(weekStart, -1))} className="p-2 rounded-full hover:bg-brand-mint/20 transition-colors" data-testid="prev-week-btn">
          <CaretLeft size={20} className="text-brand-green" />
        </button>
        <div className="text-center">
          <h2 className="font-heading font-medium text-xl text-brand-text" data-testid="ementa-title">Ementa Semanal</h2>
          <p className="text-sm text-brand-muted" data-testid="week-range">{formatDateRange(weekStart)}</p>
        </div>
        <button onClick={() => setWeekStart(addWeeks(weekStart, 1))} className="p-2 rounded-full hover:bg-brand-mint/20 transition-colors" data-testid="next-week-btn">
          <CaretRight size={20} className="text-brand-green" />
        </button>
      </div>

      {/* Today button + Share + Duplicate */}
      <div className="flex items-center justify-between mb-4">
        {weekStart !== getMonday(new Date()) && (
          <button onClick={() => setWeekStart(getMonday(new Date()))} className="text-xs text-brand-green font-medium hover:underline" data-testid="today-btn">
            Voltar a esta semana
          </button>
        )}
        <div className="flex-1" />
        <div className="flex items-center gap-2">
          {menu && <ShareButton resources={{ menu: menu.id }} label="Ementa semanal" />}
          {menu && (
            <button onClick={() => window.open(`${API}/api/weekly-menus/${menu.id}/pdf`, '_blank')} className="flex items-center gap-2 text-sm text-brand-green hover:text-brand-green-hover transition-colors" data-testid="menu-pdf-btn">
              <FilePdf size={16} /> PDF
            </button>
          )}
          <button onClick={handleDuplicate} className="flex items-center gap-2 text-sm text-brand-green hover:text-brand-green-hover transition-colors" data-testid="duplicate-week-btn">
            <Copy size={16} /> Duplicar semana
          </button>
        </div>
      </div>

      {/* Toggle Fixa/Personalizada (só aparece se houver conteúdo na ementa fixa do programa) */}
      {hasFixedMenuContent && (
        <div className="flex bg-brand-bg rounded-full p-1 mb-4" data-testid="ementa-view-toggle">
          <button
            onClick={() => setView('personalizada')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-full text-xs font-medium transition-all ${view === 'personalizada' ? 'bg-white text-brand-green shadow-sm' : 'text-brand-muted'}`}
            data-testid="view-personalizada-btn"
          >
            <User size={14} /> Personalizada
          </button>
          <button
            onClick={() => setView('fixa')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-full text-xs font-medium transition-all ${view === 'fixa' ? 'bg-white text-brand-green shadow-sm' : 'text-brand-muted'}`}
            data-testid="view-fixa-btn"
          >
            <Sparkle size={14} /> Fixa do programa
          </button>
        </div>
      )}

      {view === 'fixa' && hasFixedMenuContent ? (
        <ProgramMenuView programMenu={programMenu} />
      ) : (
      <>
      {/* Days grid */}
      <div className="space-y-3">
        {DAYS.map(day => (
          <div key={day} className="bg-white border border-brand-border rounded-3xl p-4 shadow-[0_8px_32px_rgba(101,113,97,0.04)]" data-testid={`day-${day}`}>
            <h3 className="font-heading font-medium text-brand-text mb-3">{DAY_LABELS[day]}</h3>
            <div className="grid grid-cols-2 gap-3">
              {['sopa_almoco', 'almoco', 'sopa_jantar', 'jantar'].map(mealType => {
                const recipeId = menu?.meals?.[day]?.[mealType];
                const recipeName = getRecipeName(recipeId);
                return (
                  <div key={mealType}>
                    <p className="text-xs text-brand-muted mb-1.5 uppercase tracking-wider">{MEAL_LABELS[mealType]}</p>
                    {recipeName ? (
                      <div className="group relative">
                        <button
                          onClick={() => setSelectingMeal({ day, mealType })}
                          className="w-full text-left bg-brand-mint/15 border border-brand-mint/30 rounded-2xl px-3 py-2.5 text-sm text-brand-text transition-all hover:border-brand-green/40"
                          data-testid={`meal-${day}-${mealType}`}
                        >
                          <CookingPot size={14} className="inline mr-1.5 text-brand-green" weight="duotone" />
                          {recipeName}
                        </button>
                        <button
                          onClick={() => handleClearMeal(day, mealType)}
                          className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-100 text-red-500 rounded-full text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`clear-${day}-${mealType}`}
                        >
                          x
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setSelectingMeal({ day, mealType })}
                        className="w-full border-2 border-dashed border-brand-border rounded-2xl px-3 py-2.5 text-sm text-brand-placeholder hover:border-brand-green/40 hover:text-brand-muted transition-all flex items-center gap-1.5"
                        data-testid={`add-meal-${day}-${mealType}`}
                      >
                        <Plus size={14} /> Escolher
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      </>
      )}

      {/* Recipe selection dialog */}
      <RecipePickerDialog
        open={!!selectingMeal}
        onClose={() => setSelectingMeal(null)}
        recipes={recipes}
        onSelect={handleSelectRecipe}
        title={selectingMeal && `${DAY_LABELS[selectingMeal.day]} ${MEAL_LABELS[selectingMeal.mealType]}`}
      />
    </div>
  );
}
